import React, { useCallback } from 'react'
import styled from 'styled-components'

import { shortDate } from 'helpers/formatters'

import WarrantyInfoType from 'types/WarrantyInfo'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Props = {
  serialNumber: string
  warrantyInfo: WarrantyInfoType
}

const WarrantyInfo = ({ serialNumber, warrantyInfo }: Props) => {
  const getWarrantyStatus = useCallback((status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <S.Active>Active</S.Active>
      default:
        return <S.Expired>Expired</S.Expired>
    }
  }, [])

  return (
    <S.Card>
      <S.Header>
        <S.LeftHeaderLabel>Serial Number</S.LeftHeaderLabel>
        <S.RightHeaderLabel>Warranty Status</S.RightHeaderLabel>
        <S.Left><b>{serialNumber}</b></S.Left>
        <S.Right>{getWarrantyStatus(warrantyInfo.policyStatus)}</S.Right>
      </S.Header>
      <S.Body>
        <S.FullWidthRow>
          <S.Section>
            <S.ModelLabel>Model Number</S.ModelLabel>
            <div>{warrantyInfo.modelNumber}</div>
          </S.Section>
        </S.FullWidthRow>

        <S.HalfWidthRow>
          <S.Section>
            <S.Label>Policy Type</S.Label>
            <div>{warrantyInfo.policyType}</div>

            <S.Label>Policy Code</S.Label>
            <div>{warrantyInfo.policyCode}</div>

            <S.Label>Sales Order</S.Label>
            <div>{warrantyInfo.salesOrderNumber}</div>
          </S.Section>
          <S.Section>
            <S.Label>Coverage</S.Label>
            <div>{warrantyInfo.coverage}</div>

            <S.Label>Start Date</S.Label>
            <div>{shortDate(warrantyInfo.startDate)}</div>

            <S.Label>End Date</S.Label>
            <div>{shortDate(warrantyInfo.endDate)}</div>
          </S.Section>
        </S.HalfWidthRow>
      </S.Body>
    </S.Card>
  )
}

export default WarrantyInfo

const CardHalf = styled.div`
  border: 1px solid ${color('border')};
  border-radius: 10px;
  padding: 15px 20px 20px 20px;
  width: fit-content;
`

const HeaderLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  color: ${color('secondaryText')};
  margin-bottom: -5px;
`

const Label = styled.div`
  font-weight: bold;
  text-wrap: nowrap;
`

const S = {
  Card: styled.div`
    display: grid;
    grid-template-columns: 1fr;
    width: fit-content;
    box-shadow: rgba(0,0,0,0.1) 0px 0 6px 0px;
    border-radius: 10px;

    @media ${screenSize.small} {
      width: auto;
    }
  `,
  Header: styled(CardHalf)`
    display: grid;
    grid-template-columns: 1fr 1fr;
    font-size: 20px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom: 0;
    padding-bottom: 10px;
    width: 100%;
    background-color: ${color('background')};
  `,
  HeaderLabel,
  LeftHeaderLabel: styled(HeaderLabel)`
    text-align: left;
  `,
  RightHeaderLabel: styled(HeaderLabel)`
    text-align: right;
  `,
  Left: styled.div`
    text-align: left;
  `,
  Right: styled.div`
    text-align: right;:
  `,
  Body: styled(CardHalf)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-top-right-radius: 0;
    border-top-left-radius: 0;
    padding-top: 13px;
    border-top: 0;
    background-color: ${color('paneBackground')};

    @media ${screenSize.small} {
      width: 100%;
    }
  `,
  FullWidthRow: styled.div`
    display: flex;
    flex: 1;
  `,
  HalfWidthRow: styled.div`
    display: flex;
    flex: .5;
    gap: 20px;

    @media ${screenSize.small} {
      display: unset;

      > div:not(:first-child) {
        padding-top: 10px;
      }
    }
  `,
  Section: styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 10px;
    inline-size: min-content;
  `,
  Label,
  ModelLabel: styled(Label)`
    margin-bottom: 10px;
  `,
  Active: styled.span`
    color: ${color('positiveStatus')};
    font-weight: bold;
  `,
  Expired: styled.span`
    font-weight: bold;
  `,
  NotFound: styled.span`
    color: ${color('noDataText')};
  `
}
